import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  foldHistoryPages,
  historyRows,
  resolveInstanceUrl,
  videoMetaFrom,
  HISTORY_PAGE_SIZE,
  type InstanceConfig,
} from '@visious/core';
import { fetchVideoDetails } from '../../api/videos';
import { getPlatform } from '../../platform/instance';
import { systemClock } from '../../platform/clock';
import { putVideoMeta, touchVideoMeta, useVideoMetaCache } from '../../platform/videoMetaStore';
import { useHistoryList } from '../../history/useHistoryList';
import { useHistoryMutations } from '../../history/mutations';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { describeHistoryWriteProblem, historyState, type HistoryScreenState } from './model';
import type { ProblemCopy } from '../../api/problem';

/**
 * The React binding for §8's History page. Every decision is in `model.ts` and in core; what is here is
 * the query, the lazy hydration loop, and the clock.
 *
 * The hydration loop is a near-copy of 3b's in `useSubscriptions.ts`, deliberately including **the comment
 * about why the effect has no `live` cleanup** — that reflexive guard is what discarded every response but
 * the first when 3b had it. The economics are one order of magnitude worse here (89–262 KB a row against
 * ~50 KB), so the same shape carries more weight.
 */

export interface HistoryBinding {
  readonly state: HistoryScreenState;
  readonly loadMore: () => void;
  readonly loadingMore: boolean;
  readonly retry: () => void;
  readonly refreshing: boolean;
  /** Handed to each row so it can report itself on screen. Nothing is hydrated before this fires. */
  readonly observeRow: (videoId: string, element: Element | null) => void;
  readonly remove: (videoId: string) => void;
  /** Clear-all. Still two clicks — the arming lives in `ConfirmButton` now, not in a panel here. */
  readonly clear: {
    readonly busy: boolean;
    readonly act: () => void;
  };
  /** The last write that failed, or `null`. Dismissed by the next attempt. */
  readonly writeProblem: ProblemCopy | null;
  /** The grid search — display-only, so it is state here and never in the URL. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
}

export function useHistory(): HistoryBinding {
  const list = useHistoryList();
  const cache = useVideoMetaCache();
  const mutations = useHistoryMutations();
  const { visible, observeRow } = useVisibleRows();
  const [search, setSearch] = useState('');

  const [config, setConfig] = useState<InstanceConfig | null>(null);
  const [writeProblem, setWriteProblem] = useState<ProblemCopy | null>(null);
  /**
   * Ids whose hydration answered `404` — a video that no longer exists. **Screen state rather than a
   * cache entry**, which is a deliberate choice: caching a negative would mean a video that 404s once
   * (this API relays upstream failures as 404 too) stays dead for the cache's 30-day TTL. Held per
   * session, so a reload re-asks once. The cost is one request per dead row per visit; the alternative
   * cost is a row that is permanently wrong and nothing to clear it.
   */
  const [unavailable, setUnavailable] = useState<readonly string[]>(EMPTY);
  /**
   * Ids whose hydration failed for any reason other than a `404`, so it is attempted **once per visit**.
   *
   * Found in a browser rather than reasoned about: without this, a failed row stays a visible cache miss,
   * so each *other* row's success changes the cache, recomputes the fold, and asks for the failed one
   * again. Two of the 40 rows on the test account answer `200` with zero bytes and one answers `500`, and
   * the network log showed them being re-requested as the page filled in.
   */
  const [failed, setFailed] = useState<readonly string[]>(EMPTY);
  const inFlight = useRef(new Set<string>());

  useEffect(() => {
    let live = true;
    void getPlatform().then((boot) => {
      if (live && boot.ok) setConfig(boot.platform.config);
    });
    return () => {
      live = false;
    };
  }, []);

  const listing = useMemo(() => foldHistoryPages(list.pages), [list.pages]);
  const nowEpochSec = Math.floor(systemClock.nowEpochMs() / 1000);

  const hydration = useMemo(
    () =>
      list.pending
        ? null
        : historyRows({
            ids: listing.ids,
            cache,
            visible,
            // `inFlight` is deliberately not passed even though core accepts it: reading a mutable ref
            // during render is reading a value React cannot track, and the effect below already refuses
            // to start a second request for an id it holds. Core keeps the parameter for a caller with no
            // effect-guarded loop.
            unavailable,
            failed,
            nowEpochSec,
          }),
    // `nowEpochSec` is out of the dependency list on purpose: it changes every render and the only thing
    // it decides here is TTL staleness, which does not need second resolution. Including it would
    // recompute the fold on every render and defeat the memo entirely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cache, failed, list.pending, listing.ids, unavailable, visible],
  );

  const toFetch = hydration?.fetch ?? EMPTY;
  useEffect(() => {
    if (toFetch.length === 0) return;

    for (const videoId of toFetch) {
      if (inFlight.current.has(videoId)) continue;
      inFlight.current.add(videoId);

      void (async () => {
        try {
          const boot = await getPlatform();
          if (!boot.ok) return;
          const result = await fetchVideoDetails(boot.platform, videoId);

          if (result.ok) {
            putVideoMeta([
              videoMetaFrom(result.value, Math.floor(boot.platform.clock.nowEpochMs() / 1000)),
            ]);
            return;
          }
          /*
           * A `404` is a *fact* about this row — the video is gone — and the page marks it. Anything else
           * (a transport failure, the zero-byte `200` two of the test account's rows answer, a `500`, a
           * parse failure) is recorded as "attempted and failed": the row stays a plain unhydrated row
           * with its thumbnail and its link, and is asked about again on the next visit rather than
           * during this one.
           *
           * Getting this wrong in either direction is visible. Reading every failure as "unavailable"
           * would label a whole page dead during a network blip; recording neither would leave a failed
           * row as a visible cache miss forever, which a browser showed re-firing as the rest of the page
           * hydrated.
           */
          if (result.error.kind === 'transport' && result.error.error.kind === 'video-unavailable') {
            setUnavailable((previous) => (previous.includes(videoId) ? previous : [...previous, videoId]));
          } else {
            setFailed((previous) => (previous.includes(videoId) ? previous : [...previous, videoId]));
          }
        } finally {
          inFlight.current.delete(videoId);
        }
      })();
    }
    /*
     * **No cleanup, and that is the fix rather than the omission** — 3b's finding, which applies here
     * unchanged and costs more. The reflexive `let live = true; return () => { live = false }` guard makes
     * the *first* response write to the cache, which changes `toFetch`, which re-runs the effect and flips
     * `live` on the run that started the other rows. Their responses arrive, are discarded, and
     * `inFlight` has already been cleared in `finally`, so nothing retries them: one row hydrates and the
     * rest never do.
     *
     * The guard exists to stop an unmounted component calling `setState`. `putVideoMeta` writes to a
     * module-level store instead, and a title fetched for a page the reader has left is still a title
     * worth keeping — it is the whole point of the cache. `setUnavailable` *is* a `setState`, and React
     * merely warns nothing at all for a set on an unmounted component since 18.
     */
  }, [toFetch]);

  // R14's LRU half: rendering from the cache is a use, and a cache that only records fetches sorts by
  // insertion order wearing an LRU field's name.
  const renderedIds = hydration?.rows.filter((row) => row.hydrated).map((row) => row.videoId);
  useEffect(() => {
    if (renderedIds !== undefined && renderedIds.length > 0) {
      touchVideoMeta(renderedIds, Math.floor(systemClock.nowEpochMs() / 1000));
    }
    // Keyed by the id list rather than by the array, which is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedIds?.join(',')]);

  const resolveAsset = useCallback(
    (path: string) => (config === null ? null : resolveInstanceUrl(config, path)),
    [config],
  );

  const progress = useWatchProgressLookup();

  const state = useMemo(
    () =>
      historyState({
        pages: list.pending ? null : list.pages,
        problem: list.problem,
        rows: hydration?.rows ?? null,
        unavailable: hydration?.unavailable ?? 0,
        removing: mutations.busy,
        resolveAsset,
        pageSize: HISTORY_PAGE_SIZE,
        // As of when the rows *arrived* rather than this render — 2b's rule: a page left open would creep
        // from "2 hours ago" to "3 hours ago" while the list itself is an hour stale.
        nowEpochSec: Math.floor(
          (list.updatedAtMs > 0 ? list.updatedAtMs : systemClock.nowEpochMs()) / 1000,
        ),
        progress,
        search,
      }),
    [
      hydration,
      list.pages,
      list.pending,
      list.problem,
      list.updatedAtMs,
      mutations.busy,
      progress,
      resolveAsset,
      search,
    ],
  );

  const remove = useCallback(
    (videoId: string) => {
      setWriteProblem(null);
      void mutations.remove(videoId).then((result) => {
        if (!result.ok) setWriteProblem(describeHistoryWriteProblem(result.problem, 'remove'));
      });
    },
    [mutations],
  );

  const clearAll = useCallback(() => {
    setWriteProblem(null);
    void mutations.clear().then((result) => {
      if (!result.ok) setWriteProblem(describeHistoryWriteProblem(result.problem, 'clear'));
      else {
        // The rows are gone, so both per-row records they were keyed on are meaningless now.
        setUnavailable(EMPTY);
        setFailed(EMPTY);
      }
    });
  }, [mutations]);

  const nextPage = state.kind === 'ready' ? state.nextPage : null;

  return {
    state,
    // Guarded by the model's own answer rather than by React Query's `hasNextPage`, which cannot know:
    // `nextHistoryPage` reads the last page's *size*, and that is the whole terminating proof.
    loadMore: () => {
      if (nextPage !== null && !list.fetchingNextPage) list.fetchNextPage();
    },
    loadingMore: list.fetchingNextPage,
    retry: () => void list.refetch(),
    refreshing: list.refreshing,
    observeRow,
    remove,
    clear: {
      busy: mutations.clearing,
      act: clearAll,
    },
    writeProblem,
    search,
    setSearch,
  };
}

const EMPTY: readonly string[] = [];

/**
 * Which rows are on screen — §8's "hydrate only visible rows", and the one thing on this page that cannot
 * be a pure function.
 *
 * `IntersectionObserver` rather than scroll arithmetic, for `useVisibleChannels`'s reasons: the browser
 * already computes this, it does not fire per frame, and it is correct through a resize and a collapsed
 * section without any of them being special-cased. A row that leaves the viewport stays in the set —
 * un-hydrating it would achieve nothing, since the record is already cached, and re-fetching it on the way
 * back would be the opposite of the point. What the set gates is the *first* fetch.
 *
 * The `rootMargin` is smaller than the subscription strip's 200 px. That strip is horizontal, where a
 * whole row of avatars is one gesture away; a vertical list of 40 rows with a generous margin would
 * hydrate most of a page nobody scrolled to, at up to 262 KB a row.
 */
function useVisibleRows(): {
  readonly visible: readonly string[];
  readonly observeRow: (videoId: string, element: Element | null) => void;
} {
  const [visible, setVisible] = useState<readonly string[]>(EMPTY);
  const seen = useRef(new Set<string>());
  const elements = useRef(new Map<Element, string>());
  const observer = useRef<IntersectionObserver | null>(null);

  const markSeen = useCallback((videoId: string) => {
    if (seen.current.has(videoId)) return;
    seen.current.add(videoId);
    setVisible([...seen.current]);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  /*
   * A callback ref, so this runs in the **commit** phase — which is also why the observer is built here
   * rather than lazily during render: constructing it in the render body reads and writes a ref
   * mid-render, which under Strict Mode's double render is two observers with one never disconnected.
   */
  const observeRow = useCallback(
    (videoId: string, element: Element | null) => {
      if (element === null) return;

      if (observer.current === null) {
        if (typeof IntersectionObserver === 'undefined') {
          // No `IntersectionObserver` (an old browser, a test environment): hydrate on mount rather than
          // never. Degrading to eager is a worse page; degrading to "no titles at all" is a broken one.
          markSeen(videoId);
          return;
        }
        observer.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const id = elements.current.get(entry.target);
              if (id !== undefined) markSeen(id);
            }
          },
          { rootMargin: '120px' },
        );
      }

      elements.current.set(element, videoId);
      observer.current.observe(element);
    },
    [markSeen],
  );

  return { visible, observeRow };
}
